<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { Bar, Pie } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// ─── Estado ──────────────────────────────────────────────────────────────────
const isLoading         = ref(false);
const fetchError        = ref(null);
const stats             = ref({});
const orderTypes        = ref([]);
const exploitZones      = ref([]);
const charts            = ref([]);
const showAddChartModal = ref(false);
const newChart          = ref({ field: '', type: 'bar', title: '' });

// ─── Filtros ──────────────────────────────────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];
const firstOfMonth = () =>
  new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .split('T')[0];

const dateRange         = ref({ start: firstOfMonth(), end: today() });
const filterOrderClass  = ref('');
const filterOrderTypeId = ref('');

// ─── Constantes ───────────────────────────────────────────────────────────────
const STATUS_LABELS = {
  draft:          'Borrador',
  ready:          'Listo',
  dispatched:     'Despachado',
  en_route:       'En camino',
  on_site:        'En sitio',
  field_complete: 'Completado',
  redirected:     'Redirigido',
  cancelled:      'Cancelado',
  merged:         'Fusionado',
};

const CLASS_LABELS = {
  service_order: 'OS – Orden de Servicio',
  work_order:    'OT – Orden de Trabajo',
};

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042',
  '#8884D8', '#82ca9d', '#ffc658', '#ff7c7c',
  '#a4de6c', '#d0ed57',
];

const AVAILABLE_FIELDS = [
  { key: 'status',      label: 'Estado',             statKey: 'by_status' },
  { key: 'class',       label: 'Clase (OS / OT)',     statKey: 'by_class' },
  { key: 'order_type',  label: 'Tipo de Servicio',    statKey: 'by_order_type' },
  { key: 'explotacion', label: 'Zona de Explotación', statKey: 'by_explotacion' },
];

const DEFAULT_CHARTS = [
  { id: 'default-status',      field: 'status',      type: 'bar', title: 'Por Estado' },
  { id: 'default-class',       field: 'class',       type: 'pie', title: 'Por Clase (OS / OT)' },
  { id: 'default-order-type',  field: 'order_type',  type: 'bar', title: 'Por Tipo de Servicio' },
  { id: 'default-zone',        field: 'explotacion', type: 'bar', title: 'Por Zona de Explotación' },
];

const STORAGE_KEY = 'order_reports_state';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const zoneDesc = code => {
  if (!code) return 'Sin zona';
  const z = exploitZones.value.find(z => String(z.code) === String(code));
  return z ? `${z.description} (${code})` : `Zona ${code}`;
};

const labelFnFor = field => {
  if (field === 'status')      return k => STATUS_LABELS[k] || k;
  if (field === 'class')       return k => CLASS_LABELS[k] || k;
  if (field === 'explotacion') return zoneDesc;
  return k => k;
};

const generateChartData = field => {
  const fieldDef = AVAILABLE_FIELDS.find(f => f.key === field);
  const rawObj   = stats.value[fieldDef?.statKey] || {};
  const fn       = labelFnFor(field);
  return Object.entries(rawObj)
    .map(([k, v]) => ({ label: fn(k) || k || 'Sin dato', value: v }))
    .sort((a, b) => b.value - a.value);
};

const getChartConfig = chart => {
  const data = generateChartData(chart.field);
  return {
    labels:   data.map(e => e.label),
    datasets: [{
      label:           chart.title,
      data:            data.map(e => e.value),
      backgroundColor: COLORS.slice(0, data.length),
    }],
  };
};

// ─── KPIs ─────────────────────────────────────────────────────────────────────
const kpis = computed(() => {
  const s = stats.value.by_status || {};
  return [
    { label: 'Total',       value: stats.value.total ?? 0,                                              icon: 'i-lucide-clipboard-list', color: 'text-n-brand' },
    { label: 'Borradores',  value: s.draft ?? 0,                                                        icon: 'i-lucide-file-edit',      color: 'text-n-slate-11' },
    { label: 'Despachadas', value: (s.dispatched ?? 0) + (s.en_route ?? 0) + (s.on_site ?? 0),         icon: 'i-lucide-truck',          color: 'text-n-amber-11' },
    { label: 'Completadas', value: s.field_complete ?? 0,                                               icon: 'i-lucide-check-circle-2', color: 'text-n-teal-11' },
    { label: 'Canceladas',  value: s.cancelled ?? 0,                                                    icon: 'i-lucide-x-circle',       color: 'text-n-ruby-11' },
  ];
});

// ─── Chart handlers ───────────────────────────────────────────────────────────
const handleAddChart = () => {
  if (!newChart.value.field) return;
  const fieldDef = AVAILABLE_FIELDS.find(f => f.key === newChart.value.field);
  charts.value.push({
    id:    `chart-${Date.now()}`,
    field: newChart.value.field,
    type:  newChart.value.type,
    title: newChart.value.title || fieldDef?.label || newChart.value.field,
  });
  newChart.value = { field: '', type: 'bar', title: '' };
  showAddChartModal.value = false;
};

const handleRemoveChart = id => {
  charts.value = charts.value.filter(c => c.id !== id);
};

// ─── Chart options ────────────────────────────────────────────────────────────
const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
};

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' } },
};

// ─── Fetch ────────────────────────────────────────────────────────────────────
const fetchStats = async () => {
  isLoading.value  = true;
  fetchError.value = null;
  try {
    const params = {
      created_after:  dateRange.value.start,
      created_before: dateRange.value.end,
    };
    if (filterOrderClass.value)  params.order_class          = filterOrderClass.value;
    if (filterOrderTypeId.value) params.order_type_record_id = filterOrderTypeId.value;

    const res = await ServiceOrdersAPI.reportStats(params);
    stats.value = res.data;
  } catch (err) {
    fetchError.value = err?.response?.data?.error || err.message;
  } finally {
    isLoading.value = false;
  }
};

const fetchCatalogs = async () => {
  try {
    const [otRes, ezRes] = await Promise.all([
      CeaLookupsAPI.get('order_types'),
      CeaLookupsAPI.get('exploitation_zones'),
    ]);
    orderTypes.value   = Array.isArray(otRes.data) ? otRes.data : [];
    exploitZones.value = Array.isArray(ezRes.data) ? ezRes.data : [];
  } catch { /* silent */ }
};

// ─── Persistencia ─────────────────────────────────────────────────────────────
const saveToLocalStorage = () => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    charts:            charts.value,
    dateRange:         dateRange.value,
    filterOrderClass:  filterOrderClass.value,
    filterOrderTypeId: filterOrderTypeId.value,
  }));
};

const loadFromLocalStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      charts.value           = state.charts?.length ? state.charts : [...DEFAULT_CHARTS];
      if (state.dateRange)         dateRange.value         = { ...state.dateRange, end: today() };
      if (state.filterOrderClass)  filterOrderClass.value  = state.filterOrderClass;
      if (state.filterOrderTypeId) filterOrderTypeId.value = state.filterOrderTypeId;
    } else {
      charts.value = [...DEFAULT_CHARTS];
    }
  } catch {
    charts.value = [...DEFAULT_CHARTS];
  }
};

const clearFilters = () => {
  dateRange.value         = { start: firstOfMonth(), end: today() };
  filterOrderClass.value  = '';
  filterOrderTypeId.value = '';
};

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  loadFromLocalStorage();
  await fetchCatalogs();
  fetchStats();
  watch(charts, saveToLocalStorage, { deep: true });
  watch([dateRange, filterOrderClass, filterOrderTypeId], () => {
    saveToLocalStorage();
    fetchStats();
  }, { deep: true });
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 overflow-auto p-6 gap-6 bg-n-background">

    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-n-slate-12 flex items-center gap-2">
          <span class="i-lucide-bar-chart-3" />
          Informes de Órdenes
        </h1>
        <p class="text-sm text-n-slate-11 mt-1">Estadísticas operativas de órdenes de servicio y trabajo</p>
      </div>
      <Button variant="outline" size="sm" @click="fetchStats">
        <span class="i-lucide-refresh-cw mr-2" />
        Actualizar
      </Button>
    </div>

    <!-- Filtros -->
    <div class="p-4 rounded-lg bg-n-solid-2 border border-n-weak">
      <div class="flex items-center gap-2 mb-4">
        <span class="i-lucide-filter text-n-slate-11" />
        <h3 class="font-semibold text-n-slate-12">Filtros</h3>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label class="text-xs font-medium text-n-slate-11 mb-1 block">Fecha inicio</label>
          <input
            v-model="dateRange.start"
            type="date"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
          />
        </div>
        <div>
          <label class="text-xs font-medium text-n-slate-11 mb-1 block">Fecha fin</label>
          <input
            v-model="dateRange.end"
            type="date"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
          />
        </div>
        <div>
          <label class="text-xs font-medium text-n-slate-11 mb-1 block">Tipo de servicio</label>
          <select
            v-model="filterOrderClass"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
          >
            <option value="">Todos (OS + OT)</option>
            <option value="service_order">OS – Órdenes de Servicio</option>
            <option value="work_order">OT – Órdenes de Trabajo</option>
          </select>
        </div>
        <div>
          <label class="text-xs font-medium text-n-slate-11 mb-1 block">Tipo de orden</label>
          <select
            v-model="filterOrderTypeId"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 text-sm"
          >
            <option value="">Todos los tipos</option>
            <option v-for="ot in orderTypes" :key="ot.id" :value="ot.id">
              {{ ot.code }} – {{ ot.description }}
            </option>
          </select>
        </div>
      </div>

      <div class="flex items-center justify-between mt-4 pt-4 border-t border-n-weak">
        <span class="text-sm text-n-slate-11">
          <template v-if="isLoading">Cargando...</template>
          <template v-else-if="fetchError"><span class="text-n-ruby-11">{{ fetchError }}</span></template>
          <template v-else>{{ (stats.total ?? 0).toLocaleString() }} órdenes en el período</template>
        </span>
        <Button variant="outline" size="sm" @click="clearFilters">
          <span class="i-lucide-x mr-1" />
          Limpiar
        </Button>
      </div>
    </div>

    <!-- Loading / Error -->
    <div v-if="isLoading" class="flex items-center justify-center py-16">
      <div class="text-center">
        <Spinner :size="32" />
        <p class="text-sm text-n-slate-11 mt-2">Cargando estadísticas...</p>
      </div>
    </div>

    <div v-else-if="fetchError" class="flex flex-col items-center justify-center py-12 gap-3">
      <span class="i-lucide-alert-circle text-5xl text-n-ruby-11" />
      <p class="text-n-ruby-11">{{ fetchError }}</p>
      <Button variant="outline" size="sm" @click="fetchStats">Reintentar</Button>
    </div>

    <template v-else>
      <!-- KPIs -->
      <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div
          v-for="kpi in kpis"
          :key="kpi.label"
          class="bg-n-solid-2 border border-n-weak rounded-lg p-4 flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <span :class="[kpi.icon, kpi.color, 'text-lg']" />
            <span class="text-xs font-medium text-n-slate-11">{{ kpi.label }}</span>
          </div>
          <p class="text-2xl font-bold text-n-slate-12">{{ kpi.value.toLocaleString() }}</p>
        </div>
      </div>

      <!-- Actions bar -->
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-xl font-bold text-n-slate-12">Gráficas</h2>
          <p class="text-sm text-n-slate-11">
            {{ charts.length }} gráfica{{ charts.length !== 1 ? 's' : '' }} configurada{{ charts.length !== 1 ? 's' : '' }}
          </p>
        </div>
        <Button color="blue" @click="showAddChartModal = true">
          <span class="i-lucide-plus mr-2" />
          Agregar Gráfica
        </Button>
      </div>

      <!-- Empty charts -->
      <div
        v-if="charts.length === 0"
        class="flex flex-col items-center justify-center py-16 bg-n-solid-2 rounded-lg border border-n-weak"
      >
        <span class="i-lucide-bar-chart-3 text-6xl text-n-slate-10 mb-4" />
        <h3 class="text-lg font-semibold text-n-slate-12 mb-1">Sin gráficas</h3>
        <p class="text-sm text-n-slate-11 mb-4">Agrega una gráfica para visualizar los datos</p>
        <Button color="blue" @click="showAddChartModal = true">
          <span class="i-lucide-plus mr-2" />
          Agregar Gráfica
        </Button>
      </div>

      <!-- Charts Grid -->
      <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div
          v-for="chart in charts"
          :key="chart.id"
          class="p-4 rounded-lg bg-n-solid-2 border border-n-weak"
        >
          <div class="flex items-start justify-between mb-4">
            <div>
              <h3 class="font-semibold text-n-slate-12 flex items-center gap-2">
                <span :class="chart.type === 'bar' ? 'i-lucide-bar-chart-3' : 'i-lucide-pie-chart'" />
                {{ chart.title }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ chart.type === 'bar' ? 'Gráfico de barras' : 'Gráfico de pastel' }} —
                {{ generateChartData(chart.field).reduce((s, r) => s + r.value, 0).toLocaleString() }} órdenes
              </p>
            </div>
            <Button variant="ghost" size="sm" @click="handleRemoveChart(chart.id)">
              <span class="i-lucide-trash-2 text-n-ruby-11" />
            </Button>
          </div>

          <div class="w-full h-[320px] mb-4">
            <Bar v-if="chart.type === 'bar'" :data="getChartConfig(chart)" :options="barOptions" />
            <Pie v-else :data="getChartConfig(chart)" :options="pieOptions" />
          </div>

          <!-- Tabla resumen -->
          <div class="border border-n-weak rounded-lg overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-n-solid-3">
                <tr>
                  <th class="text-left px-3 py-2 text-n-slate-11">{{ AVAILABLE_FIELDS.find(f => f.key === chart.field)?.label }}</th>
                  <th class="text-right px-3 py-2 text-n-slate-11">Cantidad</th>
                  <th class="text-right px-3 py-2 text-n-slate-11">%</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in generateChartData(chart.field)"
                  :key="row.label"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 text-n-slate-12 max-w-[200px]">
                    <span class="line-clamp-1">{{ row.label }}</span>
                  </td>
                  <td class="px-3 py-2 text-right text-n-slate-12">{{ row.value }}</td>
                  <td class="px-3 py-2 text-right text-n-slate-11">
                    {{ stats.total ? ((row.value / stats.total) * 100).toFixed(1) : 0 }}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>

  <!-- Modal: Agregar gráfica -->
  <div
    v-if="showAddChartModal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="showAddChartModal = false"
  >
    <div class="bg-n-solid-2 border border-n-weak rounded-xl p-6 w-full max-w-md shadow-xl">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4">Agregar Gráfica</h3>

      <div class="space-y-4">
        <div>
          <label class="text-sm font-medium text-n-slate-11 mb-1 block">Dimensión</label>
          <select
            v-model="newChart.field"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          >
            <option value="">Selecciona una dimensión</option>
            <option v-for="f in AVAILABLE_FIELDS" :key="f.key" :value="f.key">{{ f.label }}</option>
          </select>
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-11 mb-1 block">Tipo de gráfica</label>
          <select
            v-model="newChart.type"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          >
            <option value="bar">Barras</option>
            <option value="pie">Pastel</option>
          </select>
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-11 mb-1 block">Título (opcional)</label>
          <input
            v-model="newChart.title"
            type="text"
            placeholder="Título de la gráfica"
            class="w-full px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          />
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-6">
        <Button variant="outline" @click="showAddChartModal = false">Cancelar</Button>
        <Button color="blue" :disabled="!newChart.field" @click="handleAddChart">Agregar</Button>
      </div>
    </div>
  </div>
</template>
